import { useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  SegmentedChip,
  SegmentedChipLabel,
  SegmentedChipPlaceholder,
  SegmentedChipRemove,
  SegmentedChipSegment,
} from '@/components/atoms/SegmentedChip/SegmentedChip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import { ConditionValueEditor } from './ConditionValueEditor';
import { ConditionValueLabel } from './ConditionValueLabel';
import { COMPARISON_LABEL_KEYS, VALUELESS_COMPARISONS } from './constants';
import {
  ConditionItem,
  getAttributeConfig,
  getAttributeOption,
  isConditionItemComplete,
  isPrefixedInput,
} from './utils';

type ConditionChipProps = {
  item: ConditionItem;
  onChange: (item: ConditionItem) => void;
  onRemove: () => void;
  isNew?: boolean;
  disabled?: boolean;
  showError?: boolean;
};

export const ConditionChip = ({
  item,
  onChange,
  onRemove,
  isNew = false,
  disabled = false,
  showError = false,
}: ConditionChipProps) => {
  const { t } = useTranslation();
  const [isComparisonOpen, setIsComparisonOpen] = useState(isNew);
  const [isValueOpen, setIsValueOpen] = useState(false);
  const shouldOpenValue = useRef(false);

  const { attribute = '', comparison = '', value } = item;
  const config = getAttributeConfig(attribute);
  const input = config?.input ?? 'text';
  const isPrefixed = isPrefixedInput(input);
  const needsValue = (next: string) => !VALUELESS_COMPARISONS.includes(next) && input !== 'none';
  const shownValue = isPrefixed ? getAttributeOption(attribute) : (value ?? '');

  const getComparisonLabel = (option: string) =>
    COMPARISON_LABEL_KEYS[option] ? t(COMPARISON_LABEL_KEYS[option]) : option;

  const handleComparisonSelect = (next: string) => {
    onChange({ ...item, comparison: next, value: needsValue(next) ? value : null });
    setIsComparisonOpen(false);
    shouldOpenValue.current = needsValue(next) && !shownValue;
  };

  const handleComparisonMenuClosed = (e: Event) => {
    e.preventDefault();
    if (shouldOpenValue.current) {
      shouldOpenValue.current = false;
      setIsValueOpen(true);
    }
  };

  const handleValueChange = (next: string) => {
    if (isPrefixed && config) {
      onChange({
        ...item,
        attribute: next ? `${config.value}.${next}` : config.value,
        value: null,
      });
      return;
    }
    onChange({ ...item, value: next });
  };

  const valueLabel = shownValue ? (
    <ConditionValueLabel input={input} value={shownValue} />
  ) : (
    <SegmentedChipPlaceholder>{t('rules.editor.set_value')}</SegmentedChipPlaceholder>
  );

  const label = config ? t(config.labelKey) : attribute;
  const comparisonLabel = getComparisonLabel(comparison);

  return (
    <SegmentedChip error={showError && !isConditionItemComplete(item)}>
      <SegmentedChipLabel>{label}</SegmentedChipLabel>
      {disabled ? (
        <SegmentedChipSegment variant="comparison" disabled>
          {comparisonLabel}
        </SegmentedChipSegment>
      ) : (
        <DropdownMenu open={isComparisonOpen} onOpenChange={setIsComparisonOpen}>
          <DropdownMenuTrigger asChild>
            <SegmentedChipSegment variant="comparison">{comparisonLabel}</SegmentedChipSegment>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="w-40"
            onCloseAutoFocus={handleComparisonMenuClosed}
          >
            {(config?.comparisons ?? [comparison]).map((option) => (
              <DropdownMenuItem
                key={option}
                className={cn(option === comparison && 'bg-selected-soft')}
                onSelect={() => handleComparisonSelect(option)}
              >
                {getComparisonLabel(option)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {needsValue(comparison) &&
        (disabled ? (
          <SegmentedChipSegment variant="value" disabled>
            {valueLabel}
          </SegmentedChipSegment>
        ) : (
          <Popover open={isValueOpen} onOpenChange={setIsValueOpen}>
            <PopoverTrigger asChild>
              <SegmentedChipSegment variant="value">{valueLabel}</SegmentedChipSegment>
            </PopoverTrigger>
            <ConditionValueEditor
              input={input}
              value={shownValue}
              onChange={handleValueChange}
              onDone={() => setIsValueOpen(false)}
            />
          </Popover>
        ))}
      {!disabled && <SegmentedChipRemove label={label} onClick={onRemove} />}
    </SegmentedChip>
  );
};
